import { Component, OnInit, ViewChild, AfterViewInit, OnDestroy, ChangeDetectorRef } from '@angular/core';
import { Title } from '@angular/platform-browser';
import { AuthService } from '_services/auth.service';
import { DEPENDENCY_SECTION_IDS, QuestionsService } from '_services/questions.service';
import { Question } from '../../_models/question';
import { MatDialog } from '@angular/material/dialog';
import { QuestionComponent } from '../question/question.component';
import { QuestionSchoolsDialogComponent } from './question-schools-dialog.component';
import { PropagateQuestionDialogComponent } from './propagate-question-dialog.component';
import { MatTableDataSource, MatTable, MatColumnDef, MatHeaderCellDef, MatHeaderCell, MatCellDef, MatCell, MatHeaderRowDef, MatHeaderRow, MatRowDef, MatRow, MatNoDataRow } from '@angular/material/table';
import { MatPaginator } from '@angular/material/paginator';
import { MatSort, MatSortHeader } from '@angular/material/sort';
import { Subscription } from 'rxjs';
import { FormsModule } from '@angular/forms';
import { MatButton, MatIconButton } from '@angular/material/button';
import { NgClass, DatePipe, AsyncPipe } from '@angular/common';
import { MatFormField, MatLabel, MatInput } from '@angular/material/input';
import { AidYearComponent } from '../../_shared/aid-year/aid-year.component';
import { MatCard } from '@angular/material/card';
import { MatButtonToggle, MatButtonToggleGroup, MatButtonToggleChange } from '@angular/material/button-toggle';
import { MatIcon } from '@angular/material/icon';
import { MatMenu, MatMenuItem, MatMenuTrigger } from '@angular/material/menu';
import { MatTooltip } from '@angular/material/tooltip';

export type QuestionsAdminView = 'im' | 'fm' | 'catalog';

/** Student path lens for IM/FM defaults (matches calculator dependent/independent flags). */
export type DefaultsPath = 'dependent' | 'independent';

/** Yes/no gateway checklist inputs nested under student.dependencyStatus. */
const GATEWAY_CHECKLIST_IDS = DEPENDENCY_SECTION_IDS.filter(id => id !== 'student.dependencyStatus');

const DEPENDENCY_STATUS_ID = 'student.dependencyStatus';

export interface DefaultsRow {
  key: string;
  depth: 0 | 1;
  question: Question;
  label: string;
  idLabel?: string;
  formatText: string;
  ordinalDisplay: string;
  dateUpdated?: Date;
  metaChips: string[];
  deleted?: boolean;
  isGatewayParent?: boolean;
  /** Parent question id when this row can expand/collapse its trigger follow-ups. */
  collapseId?: string;
  /** If set, this row is hidden while that parent is collapsed. */
  parentCollapseId?: string;
  childCount?: number;
}

export interface SectionGroup {
  code: string;
  label: string;
  rows: DefaultsRow[];
}

const SECTION_ORDER = ['DS', 'HP', 'PI', 'PA', 'SF', 'SI', 'ST'];

const SECTION_LABELS: Record<string, string> = {
  DS: 'Dependency',
  HP: 'Parent Household',
  PI: 'Parent Income',
  PA: 'Parent Assets',
  SF: 'Student Finances',
  SI: 'Custom',
  ST: 'Start',
};

/** Catalog filter chips: All, core sections, then School (SI + schoolSpecific). */
const SECTION_FILTERS: { label: string; code: string }[] = [
  { label: 'All', code: 'ALL' },
  ...['DS', 'HP', 'PI', 'PA', 'SF', 'SI'].map(code => ({
    label: SECTION_LABELS[code],
    code,
  })),
  { label: 'School', code: 'SS' },
];

@Component({
  selector: 'app-questions-admin',
  templateUrl: './questions-admin.component.html',
  styleUrls: ['./questions-admin.component.css'],
  imports: [
    MatButton,
    MatIconButton,
    MatFormField,
    MatLabel,
    MatInput,
    FormsModule,
    AidYearComponent,
    MatCard,
    MatTable,
    MatSort,
    MatColumnDef,
    MatHeaderCellDef,
    MatHeaderCell,
    MatCellDef,
    MatCell,
    NgClass,
    MatSortHeader,
    MatHeaderRowDef,
    MatHeaderRow,
    MatRowDef,
    MatRow,
    MatNoDataRow,
    MatPaginator,
    DatePipe,
    AsyncPipe,
    MatButtonToggle,
    MatButtonToggleGroup,
    MatIcon,
    MatMenu,
    MatMenuItem,
    MatMenuTrigger,
    MatTooltip,
  ]
})
export class QuestionsAdminComponent implements OnInit, AfterViewInit, OnDestroy {
  showHelp = false;
  showDeleted = false;
  showQuestionId = false;
  viewMode: QuestionsAdminView = 'catalog';
  /** Defaults list lens: Dependent path by default. */
  defaultsPath: DefaultsPath = 'dependent';
  /** True when dependency status is in the current defaults path list (hosts the path toggle). */
  gatewayInView = false;
  sectionFilter = 'ALL';
  searchText = '';
  sectionGroups: SectionGroup[] = [];
  catalogCount = 0;
  baseCount = 0;
  maxCount = 0;
  /** Parent question ids whose trigger follow-ups are collapsed. */
  private collapsedParentIds = new Set<string>();
  private collapseSeeded = new Set<string>();

  readonly sectionFilters = SECTION_FILTERS;

  displayedColumns = ['question', 'schools', 'section', 'format', 'defaults', 'order', 'dateUpdated'];

  dataSource = new MatTableDataSource<Question>([]);
  questionsSubscription?: Subscription;
  indexSubscription?: Subscription;
  indexReady = false;
  rebuildingIndex = false;
  indexError = '';
  actionError = '';
  private indexAidYear: number | null = null;

  @ViewChild(MatPaginator) paginator?: MatPaginator;
  @ViewChild(MatSort) sort?: MatSort;

  constructor(
    private titleService: Title,
    public qs: QuestionsService,
    public dialog: MatDialog,
    private cd: ChangeDetectorRef,
    private auth: AuthService
  ) {
    this.titleService.setTitle('Admin Questions | NPC Admin');
  }

  ngOnInit() {
    this.indexSubscription = this.qs.questionSchoolIndex.subscribe(index => {
      this.indexReady = !!index;
    });

    this.questionsSubscription = this.qs.allQuestions.subscribe(res => {
      if (res) {
        this.refreshView();
        const aidYear = this.qs.allQuestionsLoadedAidYear;
        if (aidYear && aidYear !== this.indexAidYear) {
          this.indexAidYear = aidYear;
          this.qs.loadQuestionSchoolIndex(aidYear);
        }
      }
    });
  }

  ngAfterViewInit(): void {
    this.bindCatalogTable();
  }

  onViewChange(event: MatButtonToggleChange): void {
    this.setView(event.value as QuestionsAdminView);
  }

  setView(mode: QuestionsAdminView): void {
    this.viewMode = mode;
    this.refreshView();
    if (mode === 'catalog') {
      this.cd.detectChanges();
      this.bindCatalogTable();
    }
  }

  refreshView(): void {
    const visible = this.qs.allQuestions.value.filter(q => !q.dateDeleted || this.showDeleted);

    if (this.viewMode === 'im' || this.viewMode === 'fm') {
      const required = visible.filter(q => this.viewMode === 'im' ? q.imRequired : q.fmRequired);
      const forPath = required.filter(q =>
        this.defaultsPath === 'dependent' ? q.dependent : q.independent
      );
      this.gatewayInView = forPath.some(q => q.id === DEPENDENCY_STATUS_ID);
      this.sectionGroups = this.buildDefaultsGroups(forPath);
      this.dataSource.data = [];
      this.maxCount = forPath.length;
      this.baseCount = this.countBaseQuestions(forPath);
      return;
    }

    this.sectionGroups = [];
    this.baseCount = 0;
    this.maxCount = 0;
    this.gatewayInView = false;
    this.displayedColumns = this.sectionFilter === 'ALL'
      ? ['question', 'schools', 'section', 'format', 'defaults', 'order', 'dateUpdated']
      : ['question', 'schools', 'format', 'defaults', 'order', 'dateUpdated'];
    let list = visible;

    const search = this.searchText.trim().toLowerCase();
    if (search) {
      list = list.filter(q =>
        (q.label && q.label.toLowerCase().includes(search)) ||
        (q.id && q.id.toLowerCase().includes(search))
      );
    }

    if (this.sectionFilter === 'SI') {
      list = list.filter(q => q.section_id === 'SI' && q.schoolSpecific !== true);
    } else if (this.sectionFilter === 'SS') {
      list = list.filter(q => q.section_id === 'SI' && q.schoolSpecific === true);
    } else if (this.sectionFilter !== 'ALL') {
      list = list.filter(q => q.section_id === this.sectionFilter);
    }

    this.catalogCount = list.length;
    this.dataSource.data = list;
    if (this.dataSource.paginator) {
      this.dataSource.paginator.firstPage();
    }
  }

  viewHeading(): string {
    if (this.viewMode === 'im') {
      return 'Default questions for a new IM school';
    }
    if (this.viewMode === 'fm') {
      return 'Default questions for a new FM school';
    }
    return 'Question catalog';
  }

  viewCountLabel(): string {
    if (this.viewMode === 'catalog') {
      return `${this.catalogCount} question${this.catalogCount === 1 ? '' : 's'}`;
    }
    const pathLabel = this.defaultsPath === 'dependent' ? 'Dependent' : 'Independent';
    if (this.maxCount === 0) {
      return `${pathLabel} path · 0 questions`;
    }
    if (this.baseCount === this.maxCount) {
      return `${pathLabel} path · ${this.maxCount} question${this.maxCount === 1 ? '' : 's'}`;
    }
    return `${pathLabel} path · ${this.baseCount}–${this.maxCount} questions`;
  }

  setDefaultsPath(path: DefaultsPath, event?: Event): void {
    event?.stopPropagation();
    if (this.defaultsPath === path) {
      return;
    }
    this.defaultsPath = path;
    this.refreshView();
  }

  activeViewPrefCount(): number {
    return [this.showDeleted, this.showHelp, this.showQuestionId].filter(Boolean).length;
  }

  toggleShowDeleted(): void {
    this.showDeleted = !this.showDeleted;
    this.refreshView();
  }

  toggleShowHelp(): void {
    this.showHelp = !this.showHelp;
  }

  toggleShowQuestionId(): void {
    this.showQuestionId = !this.showQuestionId;
  }

  sectionLabel(code?: string): string {
    if (!code) {
      return 'Unknown';
    }
    return SECTION_LABELS[code] ?? code;
  }

  catalogSectionLabel(question: Question): string {
    if (question.section_id === 'SI' && question.schoolSpecific) {
      return 'School';
    }
    return this.sectionLabel(question.section_id);
  }

  formatLabel(format?: string): string {
    if (format === 'Boolean') {
      return 'Yes/No';
    }
    if (format === 'Code') {
      return 'Choices';
    }
    return format || '';
  }

  rowId(question: Question): string {
    return `qa-${(question.id || '').replace(/[^a-zA-Z0-9_-]/g, '-')}`;
  }

  isDefaultsRowVisible(row: DefaultsRow): boolean {
    if (!row.parentCollapseId) {
      return true;
    }
    return !this.collapsedParentIds.has(row.parentCollapseId);
  }

  isParentCollapsed(collapseId: string): boolean {
    return this.collapsedParentIds.has(collapseId);
  }

  toggleParentCollapse(collapseId: string, event: Event): void {
    event.stopPropagation();
    if (this.collapsedParentIds.has(collapseId)) {
      this.collapsedParentIds.delete(collapseId);
    } else {
      this.collapsedParentIds.add(collapseId);
    }
  }

  private seedParentCollapsed(collapseId: string): void {
    if (!this.collapseSeeded.has(collapseId)) {
      this.collapseSeeded.add(collapseId);
      this.collapsedParentIds.add(collapseId);
    }
  }

  addQuestion() {
    const dialogRef = this.dialog.open(QuestionComponent, {
      width: '1050px'
    });

    dialogRef.afterClosed().subscribe(saveQuestion => {
      if (saveQuestion?.id) {
        saveQuestion.dateCreated = new Date(Date.now());
        saveQuestion.dateUpdated = new Date(Date.now());
        const questions = this.qs.allQuestions.value;
        questions.push(saveQuestion);
        questions.sort((n1, n2) => n1.ordinal - n2.ordinal);
        this.qs.saveAllQuestions(questions, this.qs.allQuestionsLoadedAidYear);
        this.refreshView();
      }
    });
  }

  clickedRow(question?: Question) {
    if (!question) {
      return;
    }
    const dialogRef = this.dialog.open(QuestionComponent, {
      width: '1050px',
      data: { question }
    });

    dialogRef.afterClosed().subscribe(saveQuestion => {
      void this.finishCatalogEdit(saveQuestion);
    });
  }

  private async finishCatalogEdit(saveQuestion?: Question): Promise<void> {
    if (!saveQuestion?.id) {
      return;
    }
    const questions = this.qs.allQuestions.value;
    const index = questions.findIndex(q => q.id === saveQuestion.id);
    if (index < 0 || JSON.stringify(saveQuestion) === JSON.stringify(questions[index])) {
      this.refreshView();
      return;
    }

    saveQuestion.dateUpdated = new Date(Date.now());
    questions[index] = saveQuestion;
    this.refreshView();

    const aidYear = this.qs.allQuestionsLoadedAidYear;
    this.actionError = '';
    try {
      await this.qs.saveAllQuestions(questions, aidYear);
    } catch (err) {
      this.actionError = err instanceof Error ? err.message : 'Unable to save question';
      this.cd.detectChanges();
      return;
    }
    if (!aidYear) {
      return;
    }
    const user = await this.auth.currentUser();
    if (!user?.dev) {
      return;
    }
    this.dialog.open(PropagateQuestionDialogComponent, {
      width: '520px',
      data: {
        questionId: saveQuestion.id,
        questionLabel: saveQuestion.label,
        aidYear,
      },
    });
  }

  async rebuildIndex(): Promise<void> {
    const aidYear = this.qs.allQuestionsLoadedAidYear;
    if (!aidYear || this.rebuildingIndex) {
      return;
    }
    this.rebuildingIndex = true;
    this.indexError = '';
    try {
      await this.qs.rebuildQuestionSchoolIndex(aidYear);
      this.qs.loadQuestionSchoolIndex(aidYear);
    } catch (err) {
      this.indexError = err instanceof Error ? err.message : 'Unable to rebuild school index';
    } finally {
      this.rebuildingIndex = false;
      this.cd.detectChanges();
    }
  }

  async openSchools(event: Event, question: Question): Promise<void> {
    event.stopPropagation();
    const schoolIds = await this.qs.schoolIdsForQuestion(question.id);
    this.dialog.open(QuestionSchoolsDialogComponent, {
      width: '520px',
      data: {
        questionLabel: question.label,
        questionId: question.id,
        schoolIds,
      },
    });
  }

  ngOnDestroy() {
    this.questionsSubscription?.unsubscribe();
    this.indexSubscription?.unsubscribe();
  }

  private bindCatalogTable(): void {
    if (this.paginator) {
      this.dataSource.paginator = this.paginator;
    }
    if (this.sort) {
      this.dataSource.sort = this.sort;
    }
    this.dataSource.sortingDataAccessor = (item, property) => {
      switch (property) {
        case 'dateUpdated':
          return item.dateUpdated ? new Date(item.dateUpdated).getTime() : 0;
        case 'question':
          return item.label ? item.label.toLowerCase() : '';
        case 'section':
          return this.catalogSectionLabel(item).toLowerCase();
        case 'format':
          return this.formatLabel(item.format).toLowerCase();
        case 'defaults':
          return `${item.imRequired ? 'im' : ''}${item.fmRequired ? 'fm' : ''}${item.enhanceCalc ? 'refine' : ''}`;
        case 'schools':
          return this.qs.questionCountFor(item.id) ?? -1;
        case 'order':
          return item.ordinal ?? 0;
        default: {
          const value = item[property as keyof Question];
          return typeof value === 'string' || typeof value === 'number' ? value : '';
        }
      }
    };
  }

  private countBaseQuestions(questions: Question[]): number {
    const rolledIntoGateway = new Set<string>([...GATEWAY_CHECKLIST_IDS, DEPENDENCY_STATUS_ID]);
    let count = 0;
    let gatewayPresent = false;
    for (const question of questions) {
      if (rolledIntoGateway.has(question.id)) {
        gatewayPresent = true;
        continue;
      }
      if (question.triggerQuestionId) {
        continue;
      }
      count++;
    }
    if (gatewayPresent) {
      count++;
    }
    return count;
  }

  private buildDefaultsGroups(questions: Question[]): SectionGroup[] {
    const byCode = new Map<string, Question[]>();
    for (const question of questions) {
      const code = question.section_id || 'SI';
      const group = byCode.get(code) ?? [];
      group.push(question);
      byCode.set(code, group);
    }

    const codes = [
      ...SECTION_ORDER.filter(code => byCode.has(code)),
      ...[...byCode.keys()].filter(code => !SECTION_ORDER.includes(code)).sort(),
    ];

    return codes.map(code => ({
      code,
      label: this.sectionLabel(code),
      rows: code === 'DS'
        ? this.buildDependencyRows(byCode.get(code) ?? [])
        : this.buildTriggerTreeRows(byCode.get(code) ?? []),
    }));
  }

  private buildDependencyRows(questions: Question[]): DefaultsRow[] {
    const gatewaySet = new Set<string>(GATEWAY_CHECKLIST_IDS);
    const gatewayChildren = questions
      .filter(q => gatewaySet.has(q.id))
      .sort((a, b) => (a.ordinal ?? 0) - (b.ordinal ?? 0));
    const statusQuestion = questions.find(q => q.id === DEPENDENCY_STATUS_ID);
    const remaining = questions.filter(q => !gatewaySet.has(q.id) && q.id !== DEPENDENCY_STATUS_ID);

    const rows: DefaultsRow[] = [];
    if (statusQuestion) {
      if (gatewayChildren.length > 0) {
        this.seedParentCollapsed(statusQuestion.id);
      }
      rows.push(this.toQuestionRow(statusQuestion, 0, {
        gatewayParent: true,
        collapseId: gatewayChildren.length > 0 ? statusQuestion.id : undefined,
        childCount: gatewayChildren.length > 0 ? gatewayChildren.length : undefined,
      }));
      for (const child of gatewayChildren) {
        rows.push(this.toQuestionRow(child, 1, {
          gatewayChild: true,
          parentCollapseId: statusQuestion.id,
        }));
      }
    } else {
      // Status not in this default set — show checklist items as a normal tree.
      return this.buildTriggerTreeRows([...gatewayChildren, ...remaining]);
    }

    rows.push(...this.buildTriggerTreeRows(remaining));
    return rows;
  }

  private buildTriggerTreeRows(questions: Question[]): DefaultsRow[] {
    const sorted = [...questions].sort((a, b) => (a.ordinal ?? 0) - (b.ordinal ?? 0));
    const inSet = new Set(sorted.map(q => q.id));
    const childrenByParent = new Map<string, Question[]>();
    const roots: Question[] = [];

    for (const question of sorted) {
      const parentId = question.triggerQuestionId;
      if (parentId && inSet.has(parentId)) {
        const kids = childrenByParent.get(parentId) ?? [];
        kids.push(question);
        childrenByParent.set(parentId, kids);
      } else {
        roots.push(question);
      }
    }

    const rows: DefaultsRow[] = [];
    for (const root of roots) {
      const kids = (childrenByParent.get(root.id) ?? []).sort((a, b) => (a.ordinal ?? 0) - (b.ordinal ?? 0));
      if (kids.length > 0) {
        this.seedParentCollapsed(root.id);
      }
      rows.push(this.toQuestionRow(root, 0, {
        collapseId: kids.length > 0 ? root.id : undefined,
        childCount: kids.length > 0 ? kids.length : undefined,
      }));
      for (const child of kids) {
        rows.push(this.toQuestionRow(child, 1, { parentCollapseId: root.id }));
      }
    }

    // Orphans whose parent is missing from this set still appear as roots above.
    return rows;
  }

  private toQuestionRow(
    question: Question,
    depth: 0 | 1,
    options?: {
      gatewayParent?: boolean;
      gatewayChild?: boolean;
      collapseId?: string;
      parentCollapseId?: string;
      childCount?: number;
    }
  ): DefaultsRow {
    const metaChips: string[] = [];
    if (this.viewMode === 'im' && !question.fmRequired) {
      metaChips.push('IM Specific');
    }
    if (this.viewMode === 'fm' && !question.imRequired) {
      metaChips.push('FM Specific');
    }
    if (depth === 1 && question.triggerQuestionId && !options?.gatewayChild) {
      metaChips.push('Follow-up');
    }

    return {
      key: question.id,
      depth,
      question,
      label: question.label,
      idLabel: question.id,
      formatText: options?.gatewayParent ? 'Checklist' : this.formatLabel(question.format),
      ordinalDisplay: String(question.ordinal ?? ''),
      dateUpdated: question.dateUpdated,
      metaChips,
      deleted: !!question.dateDeleted,
      isGatewayParent: !!options?.gatewayParent,
      collapseId: options?.collapseId,
      parentCollapseId: options?.parentCollapseId,
      childCount: options?.childCount,
    };
  }
}
